// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";

import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import StyledTitle, { StyledTitleStyles } from "@synd/App/Components/Common/StyledTitle";
import Skeleton from "@synd/App/Components/Skeleton/Skeleton";
// import { TitleTypographyStyles } from "@synd/App/Components/Common/TitleTypography";
import { CustomAppState } from "@synd/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@synd/MobiusOverrides/Button/Button";
import DataTable, { DataTableProps } from "@synd/MobiusOverrides/DataTable/DataTable";
import DataTableHead, { DataTableHeadProps } from "@synd/MobiusOverrides/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@synd/MobiusOverrides/DataTable/DataTableHeader";
import { DataTableRowProps } from "@synd/MobiusOverrides/DataTable/DataTableRow";
import OrderSummaryCard, {
    OrderSummaryCardStyles,
    RecentOrderTableProperties,
} from "@synd/Overrides/Components/OrderSummaryCard";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    // XNG-Changes
    showOrderNumber = "showOrderNumber",
    showDate = "showDate",
    showOrderTotal = "showOrderTotal",
    showStatus = "showStatus",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showReorderProducts = "showReorderProducts",
    showTitle = "showTitle",
    showLink = "showLink",
    showLinkAsAButtonUnderTheTable = "showLinkAsAButtonUnderTheTable",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.showOrderNumber]: boolean;
        [fields.showDate]: boolean;
        [fields.showOrderTotal]: boolean;
        [fields.showStatus]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showReorderProducts]: boolean;
        [fields.showTitle]: boolean;
        [fields.showLink]: boolean;
        [fields.showLinkAsAButtonUnderTheTable]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // XNG-Change
        tabs: (state as CustomAppState).xTabs,
        isTablet: (state as CustomAppState).xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    loadOrders,
    // XNG-Change
    updateZone: (zone: string, enabled: boolean) => ({
        type: "xTabs/EnableZone",
        data: {
            zone,
            isEnabled: enabled, // we need to show this tab if we are in tab context
        },
    }),
};

// XNG-Changes: Added HasHistory type
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    // XNG-Changes
    titleTypographyStyles?: StyledTitleStyles;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    ViewAllMyOrders?: ButtonPresentationProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}

.order-summary-card-grid-item{
}

.order-summary-card-container{
}

.page-type-link-grid-item{
}

.order-number-grid-item{
}

.status-grid-item{
}

.web-order-number-grid-item{
}

.total-grid-item{
}`;

let recentOrdersParameter: GetOrdersApiParameter = {
    page: 1,
    sort: "OrderDate DESC",
    customerSequence: "-1",
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }
    // XNG-change
    componentDidUpdate(): void {
        if (this.props.zone?.includes("tab")) {
            if (this.props.canViewOrders && this.props.ordersDataView.value && !this.props.tabs[this.props.zone]) {
                this.props.updateZone(this.props.zone, true);
            } else if (!this.props.canViewOrders || !this.props.ordersDataView.value) {
                this.props.updateZone(this.props.zone, false);
            }
        }
    }

    render() {
        // XNG-Changes: handle View All My Order click
        const handleOnClick = (url?: string) => {
            if (!url) {
                return;
            }
            this.props.history.push(url);
        };

        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;

        // XNG-Change
        if (canViewOrders && ordersDataView.isLoading) {
            return <Skeleton height="150px" />
        }

        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        // XNG-Changes:
        const tableProperties: RecentOrderTableProperties = {
            date: {
                show: this.props.fields.showDate,
                title: translate("Order Date"),
            },
            orderNumber: {
                show: this.props.fields.showOrderNumber,
                title: translate("Order #"),
            },
            status: {
                show: this.props.fields.showStatus,
                title: translate("Status"),
            },
            orderTotal: {
                show: this.props.fields.showOrderTotal,
                title: translate("Total"),
            },
            shipTo: {
                show: this.props.fields.showShipTo,
                title: translate("Ship-To"),
            },
            pONumber: {
                show: this.props.fields.showPONumber,
                title: translate("PO #"),
            },
            reorderProducts: {
                show: this.props.fields.showReorderProducts,
                title: translate(""),
            },
        };

        const linkLabel = translate("View All My Orders");
        return (
            <StyledWrapper {...customCssWrapper}>
                {/* XNG-Changes: Commented Title for Recent Order */}
                {/* <TitleTypography
                    extendedStyles={styles.titleTypographyStyles}
                    linkText={translate("View All")}
                    link={orderHistoryUrl}
                >
                    {translate("Recent Orders")}
                </TitleTypography> */}
                {(this.props.fields?.showTitle || this.props.fields?.showLink) && (
                    <StyledTitle
                        title={this.props.fields?.showTitle ? translate("Recent Orders") : ""}
                        buttonLink={orderHistoryUrl}
                        buttonTitle={
                            this.props.fields?.showLink && !this.props.fields?.showLinkAsAButtonUnderTheTable
                                ? linkLabel
                                : ""
                        }
                        variant="h2"
                        titleAlignment="left"
                        extendedStyles={styles.titleTypographyStyles}
                    ></StyledTitle>
                )}

                {ordersDataView.value.length === 0 && (
                    <GridItem {...styles.noOrdersFoundGridItem}>
                        <Typography {...styles.noOrdersFoundText}>{translate("No orders found")}</Typography>
                    </GridItem>
                )}
                {ordersDataView.value.length > 0 && (
                    <DataTable {...styles.dataTable} isForcedResponsiveDesign={this.props.isTablet}>
                        <DataTableHead {...styles.dataTableHead}>
                            {Object.keys(tableProperties).map(key => {
                                const { show, title } = (tableProperties as any)[key];
                                return show && <DataTableHeader {...styles.dataTableHeaders}>{title}</DataTableHeader>;
                            })}
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {ordersDataView.value.map(
                                order =>
                                    order !== null && (
                                        <OrderSummaryCard
                                            key={order.id}
                                            order={order}
                                            orderSettings={settingsCollection.orderSettings}
                                            tableProperties={tableProperties}
                                            extendedStyles={styles.orderSummaryCard}
                                        />
                                    ),
                            )}
                        </DataTableBody>
                    </DataTable>
                )}
                {/* <XNG-Changes> */}
                {this.props.fields.showLinkAsAButtonUnderTheTable && (
                    <Button
                        variant={"link" as any}
                        onClick={() => handleOnClick(orderHistoryUrl)}
                        {...styles.ViewAllMyOrders}
                    >
                        {linkLabel}
                    </Button>
                )}
                {/* </XNG-Changes> */}
            </StyledWrapper>
        );
    }
}

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    // XNG-Changes: Added WithHistory
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RecentOrders)),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
            // XNG-Changes
            {
                name: fields.showOrderNumber,
                displayName: "Order #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showDate,
                displayName: "Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showOrderTotal,
                displayName: "Order Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showReorderProducts,
                displayName: "Reorder",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            // <XNG-Changes>
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.showLinkAsAButtonUnderTheTable,
                displayName: "Show Link As A Button Under The Table",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.showLink,
                displayName: "Show Link",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: advancedTab,
                isVisible: item => !item.fields?.showLinkAsAButtonUnderTheTable,
            },

            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },

            // </XNG-Changes>
        ],
    },
};

export default widgetModule;
